/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from "@angular/common";
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from "@fabric/components/message";
import {DISMISS_MSG, GMTVP} from "../../../common/constants";
import {getDefaultYearRange, range} from "../../../core/helpers/generic-helpers";
import {LoadingCounter} from "../../../core/helpers/loading-counter";
import {StateService} from "../../../core/state/state.service";
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleQueryRequestDTO} from "../../../models/vehicle-query-request-dto.model";
import {ExcelExportService} from "../../../services/excel-export.service";
import {VehicleScenarioService} from "../../../services/vehicle-scenario.service";
import {VehicleForecastQuickEditComponent} from './vehicle-forecast-quick-edit/vehicle-forecast-quick-edit.component';

@Component({
    selector: 'app-query-vehicle',
    templateUrl: './query-vehicle.component.html',
    styleUrl: './query-vehicle.component.scss',
    standalone: false
})
export class QueryVehicleComponent implements OnInit {
  REGION = 'REGION';
  MKTAREA = 'MKTAREA'
  SUBREGION = 'SUBREGION';
  PLNGMKT = 'PLNGMKT';
  SGMNT = 'SGMNT';
  MKTDIV = 'MKTDIV';
  BRANDFAM = 'BRANDFAM';
  PRGMCODE = 'PRGMCODE';
  BODYSTYLE = 'BODYSTYLE';
  TOS = 'TOS';
  ALL = '(All)';

  readonly loading = new LoadingCounter();
  includeTotals: boolean = false;

  // MultiSelect Filters
  regionFilter: SelectableOptions = new SelectableOptions();
  subregionFilter: SelectableOptions = new SelectableOptions();
  plngMktFilter: SelectableOptions = new SelectableOptions();
  segmentFilter: SelectableOptions = new SelectableOptions();
  mktDivFilter: SelectableOptions = new SelectableOptions();
  brandfamFilter: SelectableOptions = new SelectableOptions();
  bodyStyleFilter: SelectableOptions = new SelectableOptions();
  prgmCodeFilter: SelectableOptions = new SelectableOptions();
  tosFilter: SelectableOptions = new SelectableOptions();

  // Selected filters
  selectedByType: string = 'By Subregion';
  selectedMktArea: string;
  selectedSgmntLvl: string = 'Total Vehicle';
  selectedStartYear: number;
  selectedEndYear: number;
  shadeByPortfolio: true;

  queryVehicleData: any = [];
  queryVehicleFilteredData: any = [];

  // Filter options
  scenarioYearRange: number[] = [];
  mktAreaFilterOptions: string[] = [];

  toolTip = 'Selecting Planning Markets in the Planning Market filter will yield ' +
    'individual workbooks for each market.\n\n' +
    'If no Planning Markets are selected, one report will be generated and the Industry ' +
    'for sum of the markets will present on the report.';


  constructor(readonly vehicleScenarioService: VehicleScenarioService,
              readonly stateService: StateService,
              readonly excelExportService: ExcelExportService,
              public snackBar: FabMessageService,
              public dialog: MatDialog) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.shadeByPortfolio = true;
    this.getFiltersForQueryVehicle();
    this.getYearRangeForVehScen();
  }

  /**
   * Get the data for the filter dropdowns
   */
  getFiltersForQueryVehicle(): void {
    this.loading.track(this.vehicleScenarioService.getFiltersForQueryVehicle(this.stateService.getUserGmid(),
      this.stateService.selectedScenario.vehScenId)).subscribe({
      next: (data) => {
        this.queryVehicleData = data;
        this.queryVehicleFilteredData = data;
      },
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.initializeFilterOptions();
      }
    })
  }

  /**
   * Gets the year range for a vehicle scenario
   */
  getYearRangeForVehScen(): void {
    this.loading.track(this.vehicleScenarioService.getYearRange(this.stateService.selectedScenario.vehScenId)).subscribe(
      (data: any[]) => {
        // If the vehicle scenario has an existing year range, year range is +- 20 existing year range
        if (data && data.length > 0) {
          // Year range is +- 20 of the current year range of the scenario
          // If the default year range is a valid range of volumes in the scenario, then use the default year range. else use the year range values from the scenario
          const scenarioMinYear = data[0].MIN_YEAR;
          const scenarioMaxYear = data[0].MAX_YEAR;
          const {startYear, endYear} = getDefaultYearRange();
          if (startYear < scenarioMaxYear) {
            this.selectedStartYear = Math.max(scenarioMinYear, startYear);
            this.selectedEndYear = Math.min(endYear, scenarioMaxYear);
            this.scenarioYearRange = range(Math.min(scenarioMinYear, startYear) - 20, Math.max(endYear, scenarioMaxYear) + 21);
          } else {
            this.selectedStartYear = scenarioMinYear;
            this.selectedEndYear = scenarioMaxYear;
            this.scenarioYearRange = range(this.selectedStartYear - 20, this.selectedEndYear + 21);
          }
        } else {
          // else year range is +- 20 of the current year
          const currentYear = new Date().getFullYear();
          this.scenarioYearRange = range(currentYear - 20, currentYear + 21);
        }
      }, err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      });
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regionFilter.options = this.setFilterOption(this.REGION);
    this.setCascadingFilterOptions(this.REGION);
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): any[] {
    return [... new Set(this.queryVehicleFilteredData
      .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
      .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))].map(value => {
      return value;
    });
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        this.subregionFilter.options = this.setFilterOption(this.SUBREGION);
        this.mktAreaFilterOptions = this.setFilterOption(this.MKTAREA);
        this.selectedMktArea = this.mktAreaFilterOptions[0];
      // eslint-disable-next-line
      case this.SUBREGION:
        this.plngMktFilter.options = this.setFilterOption(this.PLNGMKT);
      // eslint-disable-next-line
      case this.MKTAREA:
        this.plngMktFilter.options = this.setFilterOption(this.PLNGMKT);
      // eslint-disable-next-line
      case this.PLNGMKT:
        // Change segment filter options depending on selected segment level
        if (this.selectedSgmntLvl === "Global Segment") {
          this.segmentFilter.options = this.setFilterOption('GSEG');
        } else if (this.selectedSgmntLvl === "Propulsion Segment") {
          this.segmentFilter.options = this.setFilterOption('LSEG');
        } else {
          this.segmentFilter.options = ['VEHICLE'];
        }
      // eslint-disable-next-line
      case this.SGMNT:
        this.mktDivFilter.options = this.setFilterOption(this.MKTDIV);
      // eslint-disable-next-line
      case this.MKTDIV:
        this.brandfamFilter.options = this.setFilterOption(this.BRANDFAM);
      // eslint-disable-next-line
      case this.BRANDFAM:
        this.bodyStyleFilter.options = this.setFilterOption(this.BODYSTYLE);
      // eslint-disable-next-line
      case this.BODYSTYLE:
        this.prgmCodeFilter.options = this.setFilterOption(this.PRGMCODE);
      // eslint-disable-next-line
      case this.PRGMCODE:
        this.tosFilter.options = this.setFilterOption(this.TOS);
    }
  }

  /**
   * Reset the selected options from the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  cascadeResetSelectedFilter(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        if (this.subregionFilter.selectedOptions.length > 0) {
          this.subregionFilter.selectedOptions = [];
        }
        if (this.mktAreaFilterOptions.length > 0) {
          this.selectedMktArea = this.mktAreaFilterOptions[0];
        }
      // eslint-disable-next-line
      case this.SUBREGION:
        if (this.plngMktFilter.selectedOptions.length > 0) {
          this.plngMktFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.MKTAREA:
        if (this.plngMktFilter.selectedOptions.length > 0) {
          this.plngMktFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PLNGMKT:
        if (this.segmentFilter.selectedOptions.length > 0) {
          this.segmentFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.SGMNT:
        if (this.mktDivFilter.selectedOptions.length > 0) {
          this.mktDivFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.MKTDIV:
        if (this.brandfamFilter.selectedOptions.length > 0) {
          this.brandfamFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.BRANDFAM:
        if (this.bodyStyleFilter.selectedOptions.length > 0) {
          this.bodyStyleFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.BODYSTYLE:
        if (this.prgmCodeFilter.selectedOptions.length > 0) {
          this.prgmCodeFilter.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PRGMCODE:
        if (this.tosFilter.selectedOptions.length > 0) {
          this.tosFilter.selectedOptions = [];
        }
    }
  }

  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilter list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilter: any): void {
    if (selectedFilter.length > 0) {
      this.queryVehicleFilteredData = this.queryVehicleFilteredData.filter(detailRecord =>
        selectedFilter.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   *
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.queryVehicleFilteredData = this.queryVehicleData;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.cascadeResetSelectedFilter(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regionFilter.selectedOptions);
    if (this.selectedByType === 'By Mkt Area') {
      this.applyFilter(this.MKTAREA, this.selectedMktArea);
    } else {
      this.applyFilter(this.SUBREGION, this.subregionFilter.selectedOptions);
    }
    this.applyFilter(this.PLNGMKT, this.plngMktFilter.selectedOptions);
    // this.applyFilter(this.VEHTYPE, this.vehTypeFilter.selectedOptions);
    if (this.selectedSgmntLvl === "Global Segment") {
      this.applyFilter('GSEG', this.segmentFilter.selectedOptions);
    } else if (this.selectedSgmntLvl === 'Propulsion Segment') {
      this.applyFilter('LSEG', this.segmentFilter.selectedOptions);
    }
    this.applyFilter(this.MKTDIV, this.mktDivFilter.selectedOptions);
    this.applyFilter(this.BRANDFAM, this.brandfamFilter.selectedOptions);
    this.applyFilter(this.BODYSTYLE, this.bodyStyleFilter.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.prgmCodeFilter.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Exports a vehicle forecast template
   */
  createVehicleForecastTemplate(region: string, subregion: string, plngMkt: string): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    let fileName = 'VehFT_' + this.stateService.selectedScenario.vehScenId + '_' + this.stateService.selectedScenario.vehScenName;
    if (this.includeTotals) {
      fileName = fileName + '_' + plngMkt + '_'
    }
    fileName = fileName + currDate;
    const request: VehicleQueryRequestDTO = {
      gmid: this.stateService.getUserGmid(),
      region: this.getResultString(region),
      mktAreaFlag: this.selectedByType === 'By Mkt Area' ? 1 : 0,
      vehScenId: this.stateService.selectedScenario.vehScenId,
      mktAreaSubregion: this.getResultString(this.selectedByType === 'By Mkt Area' ? this.selectedMktArea : subregion),
      plngMkt: this.getResultString(plngMkt),
      vehType: this.ALL,
      sgmntLvl: this.selectedSgmntLvl,
      sgmnt: this.getResultString(this.segmentFilter.selectedOptions.join(', ')),
      mktDiv: this.getResultString(this.mktDivFilter.selectedOptions.join(', ')),
      brandFam: this.getResultString(this.brandfamFilter.selectedOptions.join(', ')),
      bodyStyle: this.getResultString(this.bodyStyleFilter.selectedOptions.join(', ')),
      prgmCode: this.getResultString(this.prgmCodeFilter.selectedOptions.join(', ')),
      tos: this.getResultString(this.tosFilter.selectedOptions.join(', ')),
      startYear: this.selectedStartYear,
      endYear: this.selectedEndYear,
      compareScenId: -1,
      columns: '',
      filters: '',
      includeTotals: this.includeTotals,
      shadeByPortfolio: this.shadeByPortfolio,
    }
    this.loading.track(this.vehicleScenarioService.createVehicleForecastTemplate(request)).subscribe(
      (file: any) => {
        this.excelExportService.exportToExcelFromServer(file, fileName);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }, () => {
        this.snackBar.displaySuccess('Succesfully created vehicle forecast template. Exporting...', DISMISS_MSG);
      });
  }

  /**
   * Event handler for when the Export template button is clicked
   */
  onExportTemplate(): void {
    // If the selected planning market is all and multi planning market workbooks checkbox is enabled
    // create multiple vehicle forecast for each planning market
    if (this.plngMktFilter.selectedOptions.length > 0 && this.includeTotals) {
      for (const plngMkt of this.plngMktFilter.selectedOptions) {
        // Since we are creating a workbook per selected planning market, we need to get the region and subregion that is associated to the planning market
        const region = this.queryVehicleFilteredData.find(detailRecord => detailRecord.PLNGMKT === plngMkt)?.REGION;
        const subregion = this.queryVehicleFilteredData.find(detailRecord => detailRecord.PLNGMKT === plngMkt)?.SUBREGION;
        this.createVehicleForecastTemplate(region, subregion, plngMkt);
      }
    } else {
      this.createVehicleForecastTemplate(this.regionFilter.selectedOptions.join(', '),
        this.subregionFilter.selectedOptions.join(', '),
        this.plngMktFilter.selectedOptions.join(', '));
    }
  }

  /**
   * Open the quick edit popup
   */
  openQuickEditPopUp(): void {
    const request: VehicleQueryRequestDTO = {
      gmid: this.stateService.getUserGmid(),
      region: this.getResultString(this.regionFilter.selectedOptions.join(', ')),
      mktAreaFlag: this.selectedByType === 'By Mkt Area' ? 1 : 0,
      vehScenId: this.stateService.selectedScenario.vehScenId,
      mktAreaSubregion: this.getResultString(this.selectedByType === 'By Mkt Area' ? this.selectedMktArea : this.subregionFilter.selectedOptions.join(', ')),
      plngMkt: this.getResultString(this.plngMktFilter.selectedOptions.join(', ')),
      vehType: this.ALL,
      sgmntLvl: this.selectedSgmntLvl,
      sgmnt: this.getResultString(this.segmentFilter.selectedOptions.join(', ')),
      mktDiv: this.getResultString(this.mktDivFilter.selectedOptions.join(', ')),
      brandFam: this.getResultString(this.brandfamFilter.selectedOptions.join(', ')),
      bodyStyle: this.getResultString(this.bodyStyleFilter.selectedOptions.join(', ')),
      prgmCode: this.getResultString(this.prgmCodeFilter.selectedOptions.join(', ')),
      tos: this.getResultString(this.tosFilter.selectedOptions.join(', ')),
      startYear: this.selectedStartYear,
      endYear: this.selectedEndYear,
      compareScenId: -1,
      columns: '',
      filters: '',
      includeTotals: this.includeTotals,
      shadeByPortfolio: this.shadeByPortfolio,
    }

    this.dialog.open(VehicleForecastQuickEditComponent, {
      data: {
        requestDTO: request
      },
      disableClose: true,
      width: '90vw',
      height: '90vh'
    });
  }

  /**
   * If the string is empty, return a default value of '(All)' to the string
   * @param str the string to check if it is empty
   * @returns the string if it is not empty, else return '(All)'
   */
  private getResultString(str: string): string {
    if (str?.length > 0) {
      return str;
    }
    return this.ALL;
  }

  protected readonly GMTVP = GMTVP;
}
